/**
 * Theme Provider Wrapper
 * 
 * This component implements a custom theme provider that uses our global React instance.
 * It prevents the "Cannot read properties of undefined (reading 'createContext')" error
 * by ensuring all React context creation uses the same React instance.
 */

// Import React from our singleton to ensure consistent React instance
import React from '../utils/react-singleton';

// Log React availability for debugging
console.log('[ThemeProviderWrapper] React initialization:', {
  version: React?.version || 'undefined',
  available: typeof React !== 'undefined',
  createContextAvailable: typeof React?.createContext === 'function'
});

// Create a test context to verify React is working properly
try {
  const TestContext = React.createContext(null);
  console.log('[ThemeProviderWrapper] Context creation test:', TestContext ? 'Success' : 'Failed');
} catch (error) {
  console.error('[ThemeProviderWrapper] Context creation test failed:', error);
}

// Define theme types to match next-themes API
type ThemeProviderProps = {
  attribute?: string;
  defaultTheme?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
  storageKey?: string;
  themes?: string[];
  value?: Record<string, string>;
  children: React.ReactNode;
};

/**
 * Custom ThemeProvider implementation that doesn't rely on next-themes
 * This prevents the "Cannot read properties of undefined (reading 'createContext')" error
 * by implementing a simplified version using our global React instance
 */
const ThemeContext = React.createContext<{
  theme: string;
  setTheme: (theme: string) => void;
  systemTheme?: string;
}>({ 
  theme: 'system',
  setTheme: () => null,
});

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  attribute = 'data-theme',
  enableSystem = true,
  disableTransitionOnChange = false,
  themes = ['light', 'dark'],
  ...props
}) => {
  console.log('[ThemeProviderWrapper] Rendering ThemeProvider with React:', !!React);
  
  const [theme, setThemeState] = React.useState(() => {
    // Get stored theme from localStorage or use default
    if (typeof window === 'undefined') return defaultTheme;
    
    try {
      const storedTheme = localStorage.getItem(storageKey);
      return storedTheme || defaultTheme;
    } catch (e) {
      console.error('[ThemeProviderWrapper] Error reading theme from localStorage:', e);
      return defaultTheme;
    }
  });
  
  const [systemTheme, setSystemTheme] = React.useState<string | undefined>(
    enableSystem ? getSystemTheme() : undefined
  );
  
  // Handle theme changes
  const setTheme = React.useCallback((newTheme: string) => {
    setThemeState(newTheme);
    
    // Save to localStorage
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch (e) {
      console.error('[ThemeProviderWrapper] Error saving theme to localStorage:', e);
    }
  }, [storageKey]);
  
  // Apply theme to document
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    const resolvedTheme = theme === 'system' && enableSystem ? systemTheme : theme;
    
    // Apply transition blocking if enabled
    const removeTransitions = disableTransitionOnChange ? applyTransitionBlock() : null;
    
    if (attribute === 'class') {
      // Remove all theme classes
      root.classList.remove(...themes);
      // Add the current theme class
      if (resolvedTheme) {
        root.classList.add(resolvedTheme);
      }
    } else {
      // Set the theme attribute
      if (resolvedTheme) {
        root.setAttribute(attribute, resolvedTheme);
      } else {
        root.removeAttribute(attribute);
      }
    }
    
    // Set color-scheme
    if (resolvedTheme === 'dark' || resolvedTheme === 'light') {
      root.style.colorScheme = resolvedTheme;
    }
    
    // Remove transition blocking
    if (removeTransitions) {
      removeTransitions();
    }
  }, [theme, systemTheme, attribute, enableSystem, disableTransitionOnChange, themes]);
  
  // Listen for system theme changes
  React.useEffect(() => {
    if (!enableSystem) return;
    
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    
    const updateSystemTheme = () => {
      setSystemTheme(getSystemTheme());
    };
    
    // Initial check
    updateSystemTheme();
    
    // Modern event listener
    if (media.addEventListener) {
      media.addEventListener('change', updateSystemTheme);
      return () => media.removeEventListener('change', updateSystemTheme);
    } else {
      // Fallback for older browsers
      media.addListener(updateSystemTheme);
      return () => media.removeListener(updateSystemTheme);
    }
  }, [enableSystem]);
  
  const value = React.useMemo(() => ({
    theme,
    setTheme,
    systemTheme: enableSystem ? systemTheme : undefined,
  }), [theme, setTheme, systemTheme, enableSystem]);
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to get system theme
function getSystemTheme(): string {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Helper function to block transitions during theme change
function applyTransitionBlock() {
  if (typeof window === 'undefined') return () => {};
  
  const style = document.createElement('style');
  style.appendChild(document.createTextNode(
    '*{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}'
  ));
  document.head.appendChild(style);
  
  return () => {
    // Force a reflow
    window.getComputedStyle(document.body);
    
    // Remove the style after a short delay
    setTimeout(() => {
      document.head.removeChild(style);
    }, 1);
  };
}

// Hook to use the theme
export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}